import { useCallback, useEffect, useState } from 'react';
import { PAYEE_ID_PARAM } from '../constants';
import { useReceivePayeeId } from './useReceivePayeeId';

const getPayeeIdFromUrl = (): string =>
  new URLSearchParams(window.location.search).get(PAYEE_ID_PARAM) ?? '';

const updateUrlWithPayeeId = (id: string) => {
  const searchParams = new URLSearchParams(window.location.search);
  if (id) {
    searchParams.set(PAYEE_ID_PARAM, id);
  } else {
    searchParams.delete(PAYEE_ID_PARAM);
  }
  const newSearch = searchParams.toString();
  const newUrl = `${window.location.pathname}${
    newSearch ? `?${newSearch}` : ''
  }${window.location.hash}`;
  window.history.replaceState({}, '', newUrl);
};

export const useViewerPayeeIdChange = () => {
  const [apPayeeId, setApPayeeId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);

  // Initialize from URL on mount
  useEffect(() => {
    const urlPayeeId = getPayeeIdFromUrl();
    if (urlPayeeId) {
      // Set state directly to avoid infinite loop from event dispatch
      setApPayeeId(urlPayeeId);
      setIsOpen(true);
    }
  }, []);

  // Sync with URL on browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlPayeeId = getPayeeIdFromUrl();
      if (urlPayeeId && urlPayeeId !== apPayeeId) {
        // Set state directly to sync with URL
        setApPayeeId(urlPayeeId);
        setIsOpen(true);
      } else if (!urlPayeeId && isOpen) {
        // Close drawer if URL doesn't have id
        setApPayeeId('');
        setIsOpen(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [apPayeeId, isOpen]);

  const onDrawerClose = useCallback(() => {
    setIsOpen(false);
    setApPayeeId('');
    updateUrlWithPayeeId('');
  }, []);

  const dispatchEvent = useCallback(
    ({ apPayeeId }: { apPayeeId: string }) => {
      if (apPayeeId === '') {
        onDrawerClose();
      } else {
        setApPayeeId(apPayeeId);
        setIsOpen(true);
        updateUrlWithPayeeId(apPayeeId);
      }
    },
    [onDrawerClose]
  );

  useReceivePayeeId(dispatchEvent);

  return {
    apPayeeId: apPayeeId,
    isOpen,
    onDrawerClose,
  };
};
